import { css } from '@emotion/react';
import { Autocomplete, Box, Stack, styled, TextField, Typography } from '@mui/material';
import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { useThrottle } from '@uidotdev/usehooks';
import { useMap } from '@vis.gl/react-maplibre';
import { forwardRef, useMemo, useState } from 'react';
import { z } from 'zod';

import { MapLayerIcon } from '@/components/display/MapLayerIcon';
import { pickupParkingFacilityFeatureCollection } from '@/data/pickupParkingFacilityFeatureCollection';
import { MAP_LAYER_MAPPING } from '@/mappings/MAP_LAYER_MAPPING';
import { Geocoder } from '@/modules/Geocoder';
import { MAIN_BREAKPOINT } from '@/modules/MAIN_BREAKPOINT';
import { MAP_FLY_TO_ZOOM } from '@/modules/MAP_FLY_TO_ZOOM';
import { PointGuard } from '@/modules/PointGuard';
import type { ParkAndRideFacility, Station } from '@/modules/api/backend';
import { backendApiStationsFindAllOptions } from '@/modules/api/backend/@tanstack/react-query.gen';
import { geocoderApiV1AutocompleteOptions } from '@/modules/api/geocoder/@tanstack/react-query.gen';
import type { LocationFeature } from '@/types/LocationFeature';
import { zMapLayer } from '@/types/MapLayer';
import type { PickupParkingFacilityFeature } from '@/types/PickupParkingFacilityFeature';

const Root = styled(Box)(
  ({ theme }) => css`
    background-color: ${theme.palette.common.white};
    color: ${theme.palette.text.primary};
    flex: 1;

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      width: ${theme.spacing(50)};
      max-width: calc(100vw - ${theme.spacing(4)});
    }
  `,
);

const routeApi = getRouteApi('/_public/_map');

const zOptionType = z.enum([...zMapLayer.options, 'LOCATION']);
const OptionType = zOptionType.enum;

type Option =
  | {
      id: string;
      type: typeof OptionType.PARK_AND_RIDE_FACILITY;
      feature: ParkAndRideFacility;
    }
  | {
      id: string;
      type: typeof OptionType.PICKUP_PARKING_FACILITY;
      feature: PickupParkingFacilityFeature;
    }
  | {
      id: string;
      type: typeof OptionType.TRAIN_STATION;
      feature: Station;
    }
  | {
      id: string;
      type: typeof OptionType.BUS_STATION;
      feature: Station;
    }
  | {
      id: string;
      type: typeof OptionType.LOCATION;
      feature: LocationFeature;
    };

const getOptionLabel = (option: Option) => {
  if (option.type === OptionType.PICKUP_PARKING_FACILITY) {
    return option.feature.properties.featurename;
  }

  if (option.type === OptionType.LOCATION) {
    return option.feature.properties.name?.full ?? '';
  }

  return option.feature.name;
};

const getMapLayer = (option: Option) => {
  try {
    return zMapLayer.parse(option.type);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_error) {
    return undefined;
  }
};

const getOptionRank = (option: Option): number => zOptionType.options.indexOf(option.type);

export const MapSearchAutocomplete = forwardRef<HTMLInputElement>((_props, ref) => {
  const [inputValue, setInputValue] = useState('');
  const throttledInputValue = useThrottle(inputValue, 300);
  const { map: current } = useMap();
  const navigate = routeApi.useNavigate();

  const { data: stationData, isLoading: isStationsLoading } = useQuery({
    ...backendApiStationsFindAllOptions({
      query: {
        parkAndRideFacilitiesPublication: true,
        ...(throttledInputValue && { search: throttledInputValue }),
      },
    }),
    enabled: !!throttledInputValue,
  });

  const pickupParkinFacilityOptions = useMemo(() => {
    if (!throttledInputValue) return [];

    const q = throttledInputValue.toLowerCase();

    return pickupParkingFacilityFeatureCollection.features
      .filter((feature) => {
        const name = feature.properties.featurename?.toLowerCase() ?? '';
        const city = feature.properties.city?.toLowerCase() ?? '';
        const postal = feature.properties.postcode ? String(feature.properties.postcode) : '';

        return name.includes(q) || city.includes(q) || postal.includes(q);
      })
      .map(
        (feature): Option => ({
          id: feature.properties.id,
          type: OptionType.PICKUP_PARKING_FACILITY,
          feature,
        }),
      );
  }, [throttledInputValue]);

  const { data: geocoderData = [], isLoading: isGeocoderLoading } = useQuery({
    ...geocoderApiV1AutocompleteOptions({
      query: {
        text: throttledInputValue,
        size: 10,
        'boundary.rect.min_lon': 7.3,
        'boundary.rect.max_lon': 10.7,
        'boundary.rect.min_lat': 49.1,
        'boundary.rect.max_lat': 51.95,
        'focus.point.lat': 50.12125,
        'focus.point.lon': 8.636583,
        sources: Geocoder.SOURCES,
      },
    }),
    enabled: throttledInputValue !== '',
    placeholderData: keepPreviousData,
    select: (newData) =>
      newData.features.map((data) => {
        const feature = Geocoder.transformToLocationFeature(data);

        return {
          id: feature.properties.peliasGid ?? `${feature.geometry.coordinates[0]},${feature.geometry.coordinates[1]}`,
          type: OptionType.LOCATION,
          feature,
        };
      }),
  });

  const isLoadingAll = Boolean(throttledInputValue) && (isStationsLoading || isGeocoderLoading);

  const options = useMemo((): Option[] => {
    if (!throttledInputValue || isLoadingAll) {
      return [];
    }

    const stationAndFacilities: Option[] = (stationData ?? []).flatMap((feature) => [
      {
        id: feature.id,
        type: OptionType.TRAIN_STATION,
        feature,
      },
      ...(feature.parkAndRideFacilities ?? []).map((parkingFacilityFeature) => ({
        id: parkingFacilityFeature.id,
        type: OptionType.PARK_AND_RIDE_FACILITY,
        feature: parkingFacilityFeature,
      })),
    ]);

    return [...stationAndFacilities, ...pickupParkinFacilityOptions, ...geocoderData]
      .sort((a, b) => getOptionRank(a) - getOptionRank(b))
      .slice(0, 10);
  }, [geocoderData, stationData, pickupParkinFacilityOptions, throttledInputValue, isLoadingAll]);

  return (
    <Root>
      <Autocomplete<Option>
        ref={ref}
        autoComplete
        autoFocus
        autoHighlight
        blurOnSelect
        clearIcon={<XIcon />}
        filterOptions={(x) => x}
        forcePopupIcon={false}
        getOptionLabel={getOptionLabel}
        inputValue={inputValue}
        loading={isLoadingAll}
        loadingText="Suchen..."
        noOptionsText={inputValue ? 'Keine Ergebnisse' : 'Parkanlage, Station oder Adresse suchen'}
        options={options}
        value={null}
        onChange={async (_event, option) => {
          setInputValue('');

          if (!option) return;

          const { geometry } = option.feature;
          const optionId = option.id;

          if (geometry && PointGuard.hasLongitudeAndLatitude(geometry)) {
            current?.flyTo({
              center: geometry.coordinates,
              ...(current?.getZoom() < MAP_FLY_TO_ZOOM && { zoom: MAP_FLY_TO_ZOOM }),
            });
          }

          const mapLayer = getMapLayer(option);
          if (mapLayer) {
            await navigate({
              search: (prev) => ({
                ...prev,
                id: optionId,
                type: MAP_LAYER_MAPPING[mapLayer].featureType,
                layers: prev.layers ? [...new Set([...prev.layers, mapLayer])] : [mapLayer],
              }),
            });
          } else {
            await navigate({
              search: (prev) => ({
                ...prev,
                type: undefined,
                id: undefined,
              }),
            });
          }
        }}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Suche"
            placeholder="Name, Ort"
            variant="filled"
            slotProps={{
              input: {
                ...params.InputProps,
                sx: {
                  backgroundColor: 'common.white',
                },
                disableUnderline: true,
                startAdornment: (
                  <Box sx={{ marginRight: 0.5, marginTop: 1 }}>
                    <MagnifyingGlassIcon size={24} />
                  </Box>
                ),
              },
            }}
          />
        )}
        renderOption={({ key, ...optionProps }, option) => {
          const mapLayer = getMapLayer(option);

          return (
            <li key={key} {...optionProps}>
              <Stack alignItems="center" direction="row" gap={1}>
                {mapLayer ? <MapLayerIcon mapLayer={mapLayer} size={24} /> : null}
                <Typography>{getOptionLabel(option)}</Typography>
              </Stack>
            </li>
          );
        }}
      />
    </Root>
  );
});
